import React, {useContext} from "react";
import PillRow, {HeaderPillRow, PillRecord} from "../../components/pills/PillRow";
import './BranchRankingStyles.css'
import styled from "styled-components";
import TabTitle from "../tabTitle/TabTitle";
import EuropeRecord from "../../models/EuropeRecord";
import Branch from "../../interfaces/Branch";
import {GlobalDataContext} from "../../contexts/GlobalDataContext";
import {useTranslation} from "react-i18next";
import LatamRecord from "../../models/LatamRecord";
import {RegionSelectedContext, regionValues} from "../../contexts/RegionSelectedContext";
import TabHeader from "../tabHeader/TabHeader";
import TabFooter from "../tabFooter/TabFooter";
import HeaderContainer from "../Commons/HeaderContainer";
import BodyContainer from "../Commons/BodyContainer";
import FooterContainer from "../Commons/FooterContainer";
import Tab from "../tab/Tab";

interface PillsComponentProps {
  records: PillRecord[],
  year: number,
  currency?: string,
}

const StyledPillsComponent = styled.div`
  display: flex;
  flex-direction: row;

  @media (max-height: 900px) {
    font-size: 0.7rem;
  }
`;

const PillsComponent: React.FC<PillsComponentProps> = ({ records, year, currency }) => {
    return (
        <StyledPillsComponent>
            <div style={{flex: 10, display: "flex", flexDirection: "column"}}>
                <HeaderPillRow
                    year={ year }
                    isDark={ true }
                    currency= { currency }
                  />
                { records.map((record, i) => {
                    return (
                      <PillRow
                          number={ i + 1 }
                          record={ record }
                          isDark={ true }
                          key={ record.company_group }
                        />
                    );
                })}
            </div>
        </StyledPillsComponent>
    )
};

const BranchRankingContent = styled.div`
    @media (min-width: 481px) {
        display: flex;
        flex-direction: column;
        flex: 1;
        margin-top: 20px;
    }

    @media (max-width: 480px) {
        display: flex;
        flex-direction: column;
        flex: 1;
        margin-top: 31px;
        margin-bottom: 2%;
    }

    @media (max-height: 900px) {
      h3 {
        display: none;
      }
    }
`;

const HorizontalSeparator = styled.div`
  border-bottom: 4px solid white;
  margin-bottom: 20px;
  margin-top: 5px;
  min-width: 50px;
  width: 3%;

  @media (max-height: 900px) {
    display: none;
  }
`;

const parseEuropeRecords = (europeRecords: EuropeRecord[], year: number): [PillRecord[], PillRecord[]] => {
  const lifeRecords: PillRecord[] = europeRecords
    .filter((record: EuropeRecord) => record.year === year)
    .filter((record: EuropeRecord) => record.branch === Branch.LIFE)
    .sort((r1: EuropeRecord, r2: EuropeRecord) => r2.premiums_current_year - r1.premiums_current_year)
    .slice(0, 15)
    .map((x: EuropeRecord) => {
      return {
        company_group: x.company_group,
        premiums: x.premiums_current_year,
        registered_office: x.registered_office
      };
    });

  const noLifeRecords: PillRecord[] = europeRecords
    .filter((record: EuropeRecord) => record.year === year)
    .filter((record: EuropeRecord) => record.branch === Branch.NO_LIFE)
    .sort((r1: EuropeRecord, r2: EuropeRecord) => r2.premiums_current_year - r1.premiums_current_year)
    .slice(0, 15)
    .map((x: EuropeRecord) => {
      return {
        company_group: x.company_group,
        premiums: x.premiums_current_year,
        registered_office: x.registered_office
      };
    });

  return [lifeRecords, noLifeRecords];
};

const parseLatamRecords = (latamRecords: LatamRecord[], year: number): [PillRecord[], PillRecord[]] => {
  const lifeRecords: PillRecord[] = latamRecords
    .filter((record: LatamRecord) => record.year === year)
    .filter((record: LatamRecord) => record.branch === Branch.LIFE)
    .sort((r1: LatamRecord, r2: LatamRecord) => r2.premiums - r1.premiums)
    .slice(0, 15)
    .map((x: LatamRecord) => {
      return {
        company_group: x.company_group,
        premiums: x.premiums,
        registered_office: x.registered_office
      };
    });

  const noLifeRecords: PillRecord[] = latamRecords
    .filter((record: LatamRecord) => record.year === year)
    .filter((record: LatamRecord) => record.branch === Branch.NO_LIFE)
    .sort((r1: LatamRecord, r2: LatamRecord) => r2.premiums - r1.premiums)
    .slice(0, 15)
    .map((x: LatamRecord) => {
      return {
        company_group: x.company_group,
        premiums: x.premiums,
        registered_office: x.registered_office
      };
    });

  return [lifeRecords, noLifeRecords];
};


const BranchRankingComponent: React.FC<{ isTabSelected: boolean }> = ({isTabSelected}) => {
    const { t } = useTranslation();

    // @ts-ignore
    const { latamRecords, europeRecords } = useContext(GlobalDataContext);
    // @ts-ignore
    const { regionSelected, yearSelected } = useContext(RegionSelectedContext);

    if (Object.keys(europeRecords).length === 0 || Object.keys(latamRecords).length === 0)
      return <></>;

    const [lifeRecords, noLifeRecords] =
      regionSelected === regionValues.europe
        ? parseEuropeRecords(europeRecords, yearSelected)
        : parseLatamRecords(latamRecords, yearSelected);
    
    const currency = regionSelected == 'eu' ? 'M€' : 'M USD';

    const tabContent = (
      <BranchRankingContent className='content'>
          <h3>{ t('branchRanking.subtitle', { currency }) }</h3>
          <HorizontalSeparator />
          <div className={'branchRankingGraphicsContainer'}>
              <div className={'graphicContainer'}>
                  <div className={'lifeText'}>
                      { t('branchRanking.lifeRankingTitle') }
                  </div>
                  <PillsComponent
                      records={ lifeRecords }
                      year={ yearSelected }
                      currency= { currency }
                    />
              </div>
              <div className={'graphicContainer'}>
                  <div className={'lifeText'}>
                      { t('branchRanking.noLifeRankingTitle') }
                  </div>
                  <PillsComponent
                      records={ noLifeRecords }
                      year={ yearSelected }
                      currency= { currency }
                    />
              </div>
          </div>
      </BranchRankingContent>
    );

    return (
      <Tab
        isOpen={ isTabSelected }
        title={
          <TabTitle
              title={ t('branchRanking.title') }
              isTabSelected={isTabSelected}
          />
        }
        body={ tabContent }
        footer={ <TabFooter /> }
      />
    );
};

export default BranchRankingComponent;
